import React, { useEffect, useState } from 'react';
import { Card, Badge, Button, Field, inputStyle } from './movementUi';
import { Figure, Figures } from '../../../components/Figures';
import { checkOcrService } from './ocrClient';
import { territoryOf } from './movementRuns';

/*
  Movement, Setup. Built to Group G's approved drawing on 8 September 2026.

  **What every transfer this makes is stamped with**, and it has to be right
  before any page is read, because it goes onto every row of the file.

  Four changes from what was here, each one on the group's own list:

  - **"Ships from" is picked, not typed.** It was a free text box with LOU-MAIN
    already in it, and a typo silently stamped every row of the generated file
    with a warehouse that does not exist. **The owner said on 4 September 2026
    that Regional Product Movement is Louisiana AND East Texas (Tyler)**, so the
    LOU-MAIN sitting in the defaults is wrong for half of it. Picking it each
    time is the fix for both.
  - **The three figures sit on hairlines**, not in coloured pills. Rule 5.
  - **The readiness check says what to do, not what went wrong.** It printed
    whatever error came back, verbatim, where a person reads it: the running
    screen said "Cannot read properties of undefined (reading 'invoke')".
  - **The "Status" card is gone.** It repeated the same three facts as a
    sentence, underneath the three facts.
*/
export default function SetupTab({ config, setConfig, warehouses, products, mappings, refData, onReloadRefData }) {
  const [service, setService] = useState({ state: 'checking', reason: '', keyNote: '' });

  useEffect(() => {
    let alive = true;
    checkOcrService().then(r => { if (alive) setService({ state: r.ok ? 'ok' : 'off', reason: r.reason, keyNote: r.keyNote }); });
    return () => { alive = false; };
  }, []);

  const territory = territoryOf(config.sourceWarehouse);

  /* Full width, like every other screen in the group. It was capped at 860 and
     then 980, which left a third of a 1440 desk empty beside three cards. */
  return (
    <div>
      <Card
        title="On every transfer this makes"
        sub="Stamped on every row of the file, so it has to be right before any page is read."
      >
        <div className="mv-setup-row">
          <Field
            label="Ships from"
            hint={warehouses.length
              ? 'Louisiana and East Texas both use this, so it is picked each time rather than typed. '
                + 'One of your ' + warehouses.length.toLocaleString() + ' active locations.'
              : 'No active locations yet. They are kept under Warehouses.'}
          >
            {/*
              **A picker, not a box.** A typo here used to stamp every row of the
              file with a warehouse that does not exist, and nothing said so
              until the receiving system rejected the lot.
            */}
            <select
              className="fld-sel"
              style={{ ...inputStyle, appearance: 'auto' }}
              value={config.sourceWarehouse || ''}
              onChange={e => setConfig(c => ({ ...c, sourceWarehouse: e.target.value }))}
            >
              <option value="">Choose where it ships from</option>
              {warehouses.map(w => <option key={w} value={w}>{w}</option>)}
              {/*
                **Whatever is stored stays offered even when it is not on the
                list**, so a saved setting is never silently swapped for the
                first warehouse in the company. `LOU-MAIN` is exactly this case:
                it is the tool's own default and the test bench has no such
                location.
              */}
              {config.sourceWarehouse && !warehouses.includes(config.sourceWarehouse) && (
                <option value={config.sourceWarehouse}>
                  {config.sourceWarehouse} (not an active location)
                </option>
              )}
            </select>
          </Field>
          <Field label="Transferred by" hint="The name that goes on every transfer.">
            <input
              style={inputStyle}
              value={config.transferredBy}
              onChange={e => setConfig(c => ({ ...c, transferredBy: e.target.value }))}
              placeholder="Name on every transfer"
            />
          </Field>
        </div>

        {/*
          **A warehouse that is not on the list is said out loud.** Rule 9: a
          setting that looks fine and is wrong reads exactly like one that is
          right. Every row of every file this makes carries it.
        */}
        {config.sourceWarehouse && warehouses.length > 0 && !warehouses.includes(config.sourceWarehouse) && (
          <div className="mv-note held" style={{ marginTop: 4 }}>
            <div className="mv-note-title">{config.sourceWarehouse} is not one of your active locations</div>
            <div className="mv-note-says">
              Every row of every file this makes will be stamped with it, and the system reading the
              file is what will reject them. Pick one from the list above.
            </div>
          </div>
        )}
        {territory && warehouses.includes(config.sourceWarehouse) && (
          <div className="mv-setup-note">
            Runs from here will be filed under <b>{territory}</b>, taken from the name.
          </div>
        )}
      </Card>

      <Card
        title="What this reads"
        sub="Kept under Warehouses, the Item Catalog and Account Mappings. Change them there and this follows."
        right={
          <Button variant="ghost" onClick={onReloadRefData} disabled={refData.loading}>
            {refData.loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        }
        style={{ padding: 0 }}
      >
        {/* Rule 5: numbers sit on hairlines, never in tiles. They were three
            coloured pills, which is a status, and these are counts. */}
        <Figures>
          <Figure
            n={refData.loading ? '...' : warehouses.length.toLocaleString()}
            label="Active locations"
            tone={!refData.loading && !warehouses.length ? 'var(--critical-ink)' : undefined}
          />
          <Figure
            n={refData.loading ? '...' : products.length.toLocaleString()}
            label="Catalog items"
            tone={!refData.loading && !products.length ? 'var(--critical-ink)' : undefined}
          />
          <Figure n={Object.keys(mappings).length.toLocaleString()} label="Account mappings" />
        </Figures>
        {refData.error && (
          <div className="mv-setup-note" style={{ color: 'var(--critical-ink)' }}>
            These could not be read just now. Try Refresh; if it keeps happening, say so to whoever
            looks after OnHand.
          </div>
        )}
      </Card>

      <Card title="Reading handwriting" sub="Pages are read on the server, so no key is kept in your browser.">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          {service.state === 'checking' && <Badge kind="muted">Checking</Badge>}
          {service.state === 'ok' && <Badge kind="ok">Ready</Badge>}
          {service.state === 'off' && <Badge kind="err">Not set up yet</Badge>}
        </div>
        {service.state === 'off' && (
          /*
            **What to do, not what went wrong.** This printed whatever error came
            back, verbatim, where a person reads it: "Cannot read properties of
            undefined (reading 'invoke')". Nobody using OnHand can act on that
            sentence, and the one who can does not need it on this screen.
          */
          <div className="mv-setup-note">
            Everything else on this page works. Only reading handwritten photographs is blocked, and
            switching it on is a job for whoever looks after OnHand rather than something to do here.
          </div>
        )}
        {/* A key can be present and still be wrong. "Ready" only means one is
            stored, so anything suspicious about its shape is worth saying here
            rather than waiting for a page to fail. */}
        {service.state === 'ok' && service.keyNote && (
          <div className="mv-note held" style={{ marginTop: 12 }}>
            <div className="mv-note-says">{service.keyNote}</div>
          </div>
        )}
      </Card>
    </div>
  );
}
